import i18next from "i18next";
import { bffApi, DocumentCategory, type TextDocumentDto } from "../../autogen/bff-api";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";
import type { GenerateDraft, ToolContext, ToolResult } from "./tools";

export const STEP_DOCUMENT_TOOL_NAME = "createSourcingEventTextDocument";
export const STEP_DOCUMENT_PART_TOOL_NAME = "updateTextDocument";
export const STEP_DOCUMENT_DRAFT_KIND = "procurement_document";
export const DRAFT_CUT_SHORT = "DRAFT_CUT_SHORT";
export const STOPPED_BEFORE_SAVING = "STOPPED_BEFORE_SAVING";
export const DOCUMENT_NOT_ON_STEP = "DOCUMENT_NOT_ON_STEP";

export const writesStepDocument = (toolName: string): boolean =>
  toolName === STEP_DOCUMENT_TOOL_NAME || toolName === STEP_DOCUMENT_PART_TOOL_NAME;

export type StepDocument = {
  name: string;
  category: DocumentCategory;
  chapters: string[];
  chaptersNow: string[];
  shortChapters: string[];
  instructions: string;
};

export type StepDocumentPart = {
  eventId: string;
  documentId: string;
  name: string;
  chapters: string[];
  shortChapters: string[];
  instructions: string;
};

export type WrittenDraft = { draft: string; complete: boolean };

const BODY_KEY = "createSourcingEventTextDocumentRequest";
const PART_BODY_KEY = "updateTextDocumentRequest";

const NEVER_ASK_TO_PASTE = "Be ALDRI brukeren lime inn tekst, lagre en fil eller laste opp noe.";

export const isDocumentCategory = (value: unknown): value is DocumentCategory =>
  typeof value === "string" && (Object.values(DocumentCategory) as string[]).includes(value);

export const documentCategoryLabel = (category: DocumentCategory): string =>
  String(i18next.t(`documentCategory.${category}`));

export const stepDocumentOf = (args: Record<string, unknown>): StepDocument | null => {
  const document = args.document;
  if (!document || typeof document !== "object") return null;
  const { name, category, chapters, chaptersNow, shortChapters, instructions } = document as Record<string, unknown>;
  const titles = chapterTitlesOf(chapters);
  return typeof name === "string" && isDocumentCategory(category) && titles && typeof instructions === "string"
    ? {
        name,
        category,
        chapters: titles,
        chaptersNow: chaptersNowOf(chaptersNow, titles),
        shortChapters: shortChaptersOf(shortChapters, titles),
        instructions,
      }
    : null;
};

export const stepDocumentPartOf = (args: Record<string, unknown>): StepDocumentPart | null => {
  const { eventId, documentId, document } = args;
  if (typeof eventId !== "string" || typeof documentId !== "string") return null;
  if (!document || typeof document !== "object") return null;
  const { name, chapters, shortChapters, instructions } = document as Record<string, unknown>;
  const titles = chapterTitlesOf(chapters);
  return typeof name === "string" && titles && typeof instructions === "string"
    ? { eventId, documentId, name, chapters: titles, shortChapters: shortChaptersOf(shortChapters, titles), instructions }
    : null;
};

export const writeStepDocument = async (
  args: Record<string, unknown>,
  generateDraft: GenerateDraft,
  create: (args: Record<string, unknown>) => Promise<ToolResult>,
  signal?: AbortSignal,
): Promise<ToolResult> => {
  const document = stepDocumentOf(args);
  if (!document) {
    return {
      ok: false,
      deviationCode: "MISSING_FIELD",
      message: "Dokumentet trenger navn, kategori, kapitler og instructions.",
    };
  }
  const written = await generateDraft(
    { kind: STEP_DOCUMENT_DRAFT_KIND, instructions: firstPartInstructions(document) },
    signal,
  );
  if (signal?.aborted) return stoppedBeforeSaving(document.name, "så dokumentet ble ikke opprettet");
  if (!written.ok) return draftNotWritten(document, written);
  const part = finishedChapters(written.data as WrittenDraft, document.chapters, null, null, document.chaptersNow.length);
  if (!part) return firstChapterDidNotFit(document, document.chapters[0]);
  const created = await create({ ...args, text: getTiptapJsonFromMarkdown(part.markdown) });
  if (!created.ok) return created;
  const { id } = created.data as { id: string };
  return {
    ok: true,
    data: {
      document: { id, name: document.name, category: documentCategoryLabel(document.category) },
      characters: part.markdown.length,
      chaptersPlanned: document.chapters,
      chaptersWritten: part.written,
      chaptersRemaining: part.remaining,
      note: part.remaining.length === 0 ? WHOLE_DOCUMENT_NOTE : firstPartNote(document.name, id, part.remaining),
    },
  };
};

export const writeStepDocumentPart = async (
  args: Record<string, unknown>,
  generateDraft: GenerateDraft,
  ctx: ToolContext,
  save: (args: Record<string, unknown>) => Promise<ToolResult>,
  signal?: AbortSignal,
): Promise<ToolResult> => {
  const part = stepDocumentPartOf(args);
  if (!part) {
    return {
      ok: false,
      deviationCode: "MISSING_FIELD",
      message: "Delen trenger eventId, documentId, navn, kapitler og instructions.",
    };
  }
  const before = await documentOnStep(part, ctx);
  if (!before.ok) return before;
  if (before.document.name.trim() !== part.name.trim()) return otherDocumentThanConfirmed(part, before.document.name);
  const existingTitles = chapterTitlesIn(before.document.text);
  const lastNumber = lastChapterNumberIn(before.document.text);
  const existing = new Set(existingTitles.map(normalizedTitle));
  const toWrite = part.chapters.filter(
    (chapter, index) =>
      !existing.has(normalizedTitle(chapter)) &&
      part.chapters.findIndex((other) => normalizedTitle(other) === normalizedTitle(chapter)) === index,
  );
  if (toWrite.length === 0) {
    return {
      ok: true,
      data: {
        document: { id: part.documentId, name: part.name },
        chaptersAdded: [],
        chaptersRemaining: [],
        note: `«${part.name}» har allerede alle disse kapitlene, så ingenting ble lagt til. Si det til brukeren.`,
      },
    };
  }
  const written = await generateDraft(
    { kind: STEP_DOCUMENT_DRAFT_KIND, instructions: nextPartInstructions(part, existingTitles, toWrite, lastNumber) },
    signal,
  );
  if (signal?.aborted) return stoppedBeforeSaving(part.name, "så ingenting ble lagt til i dokumentet");
  if (!written.ok) return written;
  const piece = finishedChapters(written.data as WrittenDraft, toWrite, existingTitles, lastNumber);
  if (!piece) return nextChapterDidNotFit(part, toWrite[0]);
  const current = await documentOnStep(part, ctx);
  if (!current.ok) return current;
  const text = appendedDocument(current.document.text, getTiptapJsonFromMarkdown(piece.markdown));
  const saved = await save({ ...args, text });
  if (!saved.ok) return saved;
  return {
    ok: true,
    data: {
      document: { id: part.documentId, name: part.name },
      chaptersAdded: piece.written,
      chaptersRemaining: piece.remaining,
      note: piece.remaining.length === 0 ? lastPartNote(part.name) : nextPartNote(part.name, part.documentId, piece.remaining),
    },
  };
};

export const stepDocumentRequest = (args: Record<string, unknown>): Record<string, unknown> => {
  const document = stepDocumentOf(args);
  return {
    [BODY_KEY]: {
      eventId: args.eventId,
      name: document?.name ?? "",
      text: typeof args.text === "string" ? args.text : "",
      tags: [],
      category: document?.category,
    },
  };
};

export const stepDocumentPartRequest = (args: Record<string, unknown>): Record<string, unknown> => ({
  [PART_BODY_KEY]: { id: args.documentId, text: typeof args.text === "string" ? args.text : "" },
});

export const chaptersOf = (markdown: string): string[] =>
  markdown
    .split(/\r?\n/)
    .map((line) => HEADING.exec(line)?.[2])
    .filter((heading): heading is string => !!heading);

type FinishedChapters = { markdown: string; written: string[]; remaining: string[] };

type Section = { chapter: number | null; lines: string[] };

const finishedChapters = (
  { draft, complete }: WrittenDraft,
  planned: string[],
  alreadyWritten: string[] | null,
  lastNumber: number | null,
  asked = planned.length,
): FinishedChapters | null => {
  const { preamble, sections } = sectionsOf(draft, planned, alreadyWritten ?? []);
  if (sections.length === 0) {
    return complete && alreadyWritten === null
      ? { markdown: draft.trim(), written: chaptersOf(draft), remaining: planned.slice(asked) }
      : null;
  }
  const whole = (complete ? sections : sections.slice(0, -1)).filter((section) => section.chapter !== null);
  const limit = Math.min(asked, whole.length);
  let finished = 0;
  while (finished < limit && whole[finished].chapter === finished && hasBody(whole[finished])) finished++;
  if (finished === 0) return null;
  const chapters = whole
    .slice(0, finished)
    .flatMap((section, index) => [numberedHeading(section.lines[0], lastNumber, index), ...section.lines.slice(1)]);
  const kept = [...(alreadyWritten === null ? preamble : []), ...chapters];
  return {
    markdown: kept.join("\n").trim(),
    written: planned.slice(0, finished),
    remaining: planned.slice(finished),
  };
};

const hasBody = (section: Section): boolean =>
  section.lines.slice(1).some((line) => line.trim().length > 0 && !HEADING.test(line));

const LEADING_NUMBER = /^\s*(kapittel\s+)?\d+(\.\d+)*[.):-]?\s+/i;

const numberedHeading = (line: string, lastNumber: number | null, index: number): string => {
  const match = HEADING.exec(line);
  if (lastNumber === null || !match) return line;
  return `${match[1]} ${lastNumber + index + 1}. ${match[2].replace(LEADING_NUMBER, "")}`;
};

const lastChapterNumberIn = (text: string): number | null => {
  const chapters = (tiptapDocumentOf(text).content ?? []).filter(
    (node) => node.type === "heading" && node.attrs?.level === 2,
  );
  const last = chapters.length > 0 ? /^\s*(\d+)\.\s/.exec(textOf(chapters[chapters.length - 1])) : null;
  return last ? Number(last[1]) : null;
};

const appendedDocument = (existingText: string, addedText: string): string => {
  const existing = tiptapDocumentOf(existingText);
  const added = tiptapDocumentOf(addedText);
  return JSON.stringify({ ...existing, content: [...(existing.content ?? []), ...(added.content ?? [])] });
};

const HEADING = /^(#{1,3})\s+(.+?)\s*#*\s*$/;

const shortChaptersOf = (value: unknown, chapters: string[]): string[] => {
  const asked = new Set((Array.isArray(value) ? value : []).filter((title) => typeof title === "string").map(normalizedTitle));
  return chapters.filter((chapter) => asked.has(normalizedTitle(chapter)));
};

const chaptersNowOf = (value: unknown, chapters: string[]): string[] => {
  const asked = new Set((Array.isArray(value) ? value : []).filter((title) => typeof title === "string").map(normalizedTitle));
  const last = chapters.reduce((found, chapter, index) => (asked.has(normalizedTitle(chapter)) ? index : found), -1);
  return last < 0 ? chapters : chapters.slice(0, last + 1);
};

const chapterTitlesOf = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  const titles = value.filter((title): title is string => typeof title === "string" && title.trim().length > 0);
  return titles.length > 0 && titles.length === value.length ? titles.map((title) => title.trim()) : null;
};

const normalizedTitle = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[*_`]/g, "")
    .replace(LEADING_NUMBER, "")
    .replace(/[.:]+\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();

const sectionsOf = (
  draft: string,
  planned: string[],
  alreadyWritten: string[],
): { preamble: string[]; sections: Section[] } => {
  const byTitle = new Map<string, number | null>(alreadyWritten.map((title) => [normalizedTitle(title), null]));
  planned.forEach((title, index) => byTitle.set(normalizedTitle(title), index));
  const preamble: string[] = [];
  const sections: Section[] = [];
  for (const line of draft.split(/\r?\n/)) {
    const heading = HEADING.exec(line)?.[2];
    const key = heading === undefined ? undefined : normalizedTitle(heading);
    if (key !== undefined && byTitle.has(key)) {
      sections.push({ chapter: byTitle.get(key) ?? null, lines: [line] });
    } else {
      (sections[sections.length - 1]?.lines ?? preamble).push(line);
    }
  }
  return { preamble, sections };
};

type TiptapNode = { type?: string; text?: string; attrs?: { level?: number }; content?: TiptapNode[] };

const tiptapDocumentOf = (text: string): TiptapNode => {
  const parsed = parsedJson(text);
  return parsed?.type === "doc" ? parsed : (JSON.parse(getTiptapJsonFromMarkdown(text)) as TiptapNode);
};

const parsedJson = (text: string): TiptapNode | null => {
  try {
    return JSON.parse(text) as TiptapNode;
  } catch {
    return null;
  }
};

const textOf = (node: TiptapNode): string => node.text ?? (node.content ?? []).map(textOf).join("");

const chapterTitlesIn = (text: string): string[] =>
  (tiptapDocumentOf(text).content ?? [])
    .filter((node) => node.type === "heading" && (node.attrs?.level ?? 1) <= 3)
    .map(textOf)
    .filter((title) => title.trim().length > 0);

const documentOnStep = async (
  part: StepDocumentPart,
  ctx: ToolContext,
): Promise<{ ok: true; document: TextDocumentDto } | Extract<ToolResult, { ok: false }>> => {
  let listed: TextDocumentDto[];
  try {
    listed = (
      await ctx
        .dispatch(
          bffApi.endpoints.listTextDocumentsForSourcingEvent.initiate({ eventId: part.eventId }, { forceRefetch: true }),
        )
        .unwrap()
    ).documents;
  } catch {
    return {
      ok: false,
      deviationCode: "DOCUMENT_UNREADABLE",
      message: `Ingenting ble lagt til. «${part.name}» kunne ikke hentes fra steget Dokumenter. Si det til brukeren. ${NEVER_ASK_TO_PASTE}`,
    };
  }
  const document = listed.find((candidate) => candidate.id === part.documentId);
  return document
    ? { ok: true, document }
    : {
        ok: false,
        deviationCode: DOCUMENT_NOT_ON_STEP,
        message:
          `Ingenting ble lagt til. «${part.name}» finnes ikke lenger på steget Dokumenter i denne anskaffelsen. ` +
          `Si det til brukeren, og tilby å opprette dokumentet på nytt. ${NEVER_ASK_TO_PASTE}`,
      };
};

const numbered = (chapters: string[], from = 1): string =>
  chapters.map((title, index) => `${from + index}. ${title}`).join("\n");

export const SHORT_CHAPTER_WORDS = 600;

const shortChaptersInstruction = (shortChapters: string[], chapters: string[]): string => {
  const short = shortChapters.filter((title) => chapters.includes(title));
  return short.length === 0
    ? ""
    : "\n\nDisse kapitlene fikk ikke plass i én omgang og skal skrives kortfattet, med bare de viktigste kravene, " +
        `høyst ${SHORT_CHAPTER_WORDS} ord hver:\n${short.map((title) => `- ${title}`).join("\n")}`;
};

const firstPartInstructions = (document: StepDocument): string =>
  `${document.instructions}\n\nDokumentets navn: ${document.name}` +
  shortChaptersInstruction(document.shortChapters, document.chaptersNow) +
  (document.chaptersNow.length < document.chapters.length
    ? "\n\nDokumentet skrives i deler, og resten av kapitlene legges til senere. Skriv nå BARE disse kapitlene, " +
      "i denne rekkefølgen, hvert med nøyaktig denne tittelen som ##-overskrift:\n"
    : "\n\nSkriv kapitlene i denne rekkefølgen, hvert med nøyaktig denne tittelen som ##-overskrift:\n") +
  numbered(document.chaptersNow);

const nextPartInstructions = (
  part: StepDocumentPart,
  existing: string[],
  toWrite: string[],
  lastNumber: number | null,
): string =>
  `${part.instructions}\n\nDokumentets navn: ${part.name}` +
  (existing.length > 0
    ? `\n\nDokumentet har allerede disse kapitlene, som du IKKE skal skrive igjen:\n${existing.map((title) => `- ${title}`).join("\n")}`
    : "") +
  shortChaptersInstruction(part.shortChapters, toWrite) +
  "\n\nSkriv nå BARE disse kapitlene, i denne rekkefølgen, hvert med nøyaktig denne tittelen som ##-overskrift, " +
  "og uten tittel eller innledning foran det første. Hvert kapittel skal ha tekst under overskriften. Teksten " +
  "legges til etter det dokumentet har" +
  (lastNumber === null
    ? `:\n${numbered(toWrite)}`
    : `, der kapitlene er nummerert. Fortsett nummereringen fra ${lastNumber + 1}, med nummeret foran tittelen i ` +
      `overskriften, f.eks. «## ${lastNumber + 1}. ${toWrite[0]}», og underpunktene nummerert på samme måte ` +
      `(${lastNumber + 1}.1, ${lastNumber + 1}.2 osv.):\n${numbered(toWrite, lastNumber + 1)}`);

const WHOLE_DOCUMENT_NOTE =
  "Hele utkastet er skrevet i dokumentet, som nå ligger i lista på steget Dokumenter, der brukeren kan åpne " +
  "og redigere det. Si det, med dokumentets navn og kategori, og nevn kapitlene. Gjengi IKKE teksten i svaret.";

const continueOffer = (name: string, documentId: string, remaining: string[]): string =>
  `Disse ${remaining.length} kapitlene i planen er ikke skrevet ennå (chaptersRemaining): ` +
  `${remaining.map((title) => `«${title}»`).join(", ")}. Si det rett ut, og avslutt svaret med et valg som tilbyr ` +
  `å skrive dem inn i det samme dokumentet, f.eks. «- [anbefalt] Skriv de neste kapitlene i «${name}»». Velger ` +
  `brukeren det, skriver du dem med updateTextDocument: documentId ${documentId}, de samme instructions og ` +
  "chapters nøyaktig lik chaptersRemaining. Opprett ALDRI et nytt dokument for resten, heller ikke et med «del 2» " +
  `i navnet, og si aldri at dokumentet er ferdig før chaptersRemaining er tom. Gjengi IKKE teksten i svaret. ${NEVER_ASK_TO_PASTE}`;

const firstPartNote = (name: string, documentId: string, remaining: string[]): string =>
  `«${name}» er opprettet i lista på steget Dokumenter med kapitlene i chaptersWritten, skrevet ferdig. ` +
  continueOffer(name, documentId, remaining);

const nextPartNote = (name: string, documentId: string, remaining: string[]): string =>
  `Kapitlene i chaptersAdded er skrevet ferdig og lagt til etter det som sto i «${name}» fra før. ` +
  continueOffer(name, documentId, remaining);

const lastPartNote = (name: string): string =>
  `Kapitlene i chaptersAdded er lagt til etter det som sto i «${name}» fra før, og dokumentet har nå alle ` +
  "kapitlene. Si det, og at brukeren kan åpne og redigere dokumentet i lista på steget Dokumenter. Gjengi IKKE " +
  "teksten i svaret.";

const stoppedBeforeSaving = (name: string, consequence: string): ToolResult => ({
  ok: false,
  deviationCode: STOPPED_BEFORE_SAVING,
  message: `Ingenting ble lagret. Brukeren stoppet oppgaven mens utkastet til «${name}» ble skrevet, ${consequence}.`,
});

const IN_PARTS =
  "Tilby å skrive dokumentet i deler: kall createSourcingEventTextDocument på nytt med ALLE kapitlene i chapters, " +
  "akkurat som planlagt, og bare de første kapitlene i chaptersNow. Resten skrives etterpå inn i det samme " +
  "dokumentet. Kort ALDRI ned chapters, og tilby aldri et eget dokument for resten.";

const draftNotWritten = (document: StepDocument, failed: Extract<ToolResult, { ok: false }>): ToolResult =>
  document.chaptersNow.length > 1
    ? { ...failed, message: `${failed.message} Dokumentet «${document.name}» ble ikke opprettet. ${IN_PARTS} ${NEVER_ASK_TO_PASTE}` }
    : failed;

const NOT_FINISHED = "ble ikke skrevet ferdig i én omgang, og et avkuttet kapittel legges aldri i et dokument.";

const shorterOrSplit = (chapter: string, shortChapters: string[], retry: string): string =>
  shortChapters.includes(chapter)
    ? `Kapittelet var allerede bedt skrevet kortfattet. Si det rett ut, og tilby å dele «${chapter}» i to kapitler ` +
      `med hver sin tittel, f.eks. «${chapter} – del 1» og «${chapter} – del 2». Velger brukeren det, ${retry} med ` +
      "de to titlene i stedet for den ene i chapters."
    : `Si det rett ut, og tilby å skrive akkurat det kapittelet kortere, f.eks. «- [anbefalt] Skriv «${chapter}» ` +
      `kortere». Velger brukeren det, ${retry} med shortChapters: ["${chapter}"]. Resten av dokumentet skrives som ` +
      "planlagt.";

const firstChapterDidNotFit = (document: StepDocument, chapter: string): ToolResult => ({
  ok: false,
  deviationCode: DRAFT_CUT_SHORT,
  message:
    `Ingenting ble lagret. Kapittelet «${chapter}» i «${document.name}» ${NOT_FINISHED} ` +
    shorterOrSplit(chapter, document.shortChapters, "kaller du createSourcingEventTextDocument på nytt") +
    ` ${NEVER_ASK_TO_PASTE}`,
});

const nextChapterDidNotFit = (part: StepDocumentPart, chapter: string): ToolResult => ({
  ok: false,
  deviationCode: DRAFT_CUT_SHORT,
  message:
    `Ingenting ble lagt til i dokumentet. Kapittelet «${chapter}» i «${part.name}» ${NOT_FINISHED} ` +
    shorterOrSplit(
      chapter,
      part.shortChapters,
      `kaller du updateTextDocument på nytt med documentId ${part.documentId}, de samme instructions og chapters`,
    ) +
    ` ${NEVER_ASK_TO_PASTE}`,
});

const otherDocumentThanConfirmed = (part: StepDocumentPart, actualName: string): ToolResult => ({
  ok: false,
  deviationCode: DOCUMENT_NOT_ON_STEP,
  message:
    `Ingenting ble lagt til. Dokumentet med id ${part.documentId} heter «${actualName}», ikke «${part.name}», som ` +
    "brukeren bekreftet. Bruk navnet og id-en fra svaret som opprettet dokumentet, og spør brukeren hvis det er uklart " +
    `hvilket dokument resten skal inn i. ${NEVER_ASK_TO_PASTE}`,
});
